/** @jsx jsx */
import moment from 'moment'
import { Link } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { Box, Flex, Grid, Heading, jsx, Text } from 'theme-ui'
import CallToAction from '~/components/Content/Section/CallToAction'
import ListingAddToCartButton from '~/components/Listing/AddToCart/Button'
import PackCallout from '~/components/Pack/Callout'
import { useCanClub } from '~/hooks/components/use-can-club'

const EventCard = ({ event }) => {
  const {
    canClubOnly,
    ctaBook,
    ctaDetails,
    ctaPrimary,
    customLabel,
    description,
    eventDate,
    hostDescription,
    hostImage,
    hostName,
    image_rectangle,
    image_square,
    included_short,
    orderByDate,
    title,
  } = event
  const { clubDiscount } = useCanClub()

  return (
    <Box
      bg="white"
      sx={{
        borderRadius: '10px',
        overflow: 'hidden',
        transform: 'translateZ(0)',
      }}
    >
      <Grid columns={[1, 12, null, 10]} sx={{ rowGap: '0' }}>
        <Flex
          sx={{
            height: '100%',
            flexDirection: 'column',
            gridColumn: [null, '1 / 6', null, '1 / 5'],
            position: 'relative',
          }}
        >
          {image_rectangle && (
            <Box sx={{ display: [null, image_square && 'none'] }}>
              <GatsbyImage
                image={image_rectangle.gatsbyImageData}
                alt={image_rectangle.description}
              />
            </Box>
          )}
          {image_square && (
            <Box sx={{ display: [image_rectangle && 'none', 'block'] }}>
              <GatsbyImage
                image={image_square.gatsbyImageData}
                alt={image_square.description}
              />
            </Box>
          )}
          {(canClubOnly || customLabel) && (
            <Box sx={{ left: 0, position: 'absolute', top: 0 }}>
              <PackCallout
                pack={{ customLabel: customLabel || 'Members only' }}
              />
            </Box>
          )}
          {hostName && (
            <Box
              bg="backgroundMedium"
              py={[1, 3]}
              px={2}
              sx={{ flexGrow: '1' }}
            >
              <Flex
                sx={{
                  alignItems: 'center',
                  flexWrap: ['wrap', null, 'nowrap'],
                }}
              >
                {hostImage && (
                  <Box
                    mr={2}
                    sx={{
                      borderRadius: '50%',
                      flexShrink: '0',
                      height: ['64px', '72px'],
                      overflow: 'hidden',
                      transform: 'translateZ(0)',
                      width: ['64px', '72px'],
                    }}
                  >
                    <GatsbyImage
                      image={hostImage.gatsbyImageData}
                      alt={hostImage.description}
                    />
                  </Box>
                )}
                <Box my={[1, 2]} sx={{ minWidth: '160px' }}>
                  <Text as="p" m={0} variant="callout1">
                    {hostName}
                  </Text>
                  {hostDescription && (
                    <Text as="p" m={0} color="greyDark" variant="description">
                      {hostDescription}
                    </Text>
                  )}
                </Box>
              </Flex>
            </Box>
          )}
        </Flex>
        <Flex
          p={[2, 3]}
          pl={[null, 0]}
          sx={{
            flexDirection: 'column',
            gridColumn: [null, '6 / -1', null, '5 / -1'],
            justifyContent: 'center',
          }}
        >
          {eventDate && (
            <Text
              as="p"
              variant="subtitle2"
              color="greyDark"
              mt={0}
              mb={[1, 2]}
            >
              {moment(eventDate).format('dddd, MMMM DD [|] h:mm A')}
            </Text>
          )}
          <Heading as="h2" variant="title4" mt={0} mb={1}>
            {title}
          </Heading>
          {description && (
            <Box
              variant="text.body3"
              sx={{ 'p:first-of-type': { mt: 0 }, 'p:last-of-type': { mb: 0 } }}
              dangerouslySetInnerHTML={{
                __html: description.childMarkdownRemark.html,
              }}
            />
          )}
          {ctaDetails && (
            <Box mt={2}>
              <CallToAction content={ctaDetails} type="textBody3" />
            </Box>
          )}
          <Box
            mt={3}
            pt={2}
            sx={{ borderTop: '1px solid', borderTopColor: 'greyLight' }}
          >
            {included_short && (
              <Text as="p" mt={0} mb={2} variant="body3">
                <Text as="strong" sx={{ fontWeight: 'bold' }}>
                  Included:{' '}
                </Text>
                {included_short}
              </Text>
            )}
            {(ctaPrimary || ctaBook) && (
              <Flex
                mb={-2}
                sx={{
                  alignItems: 'center',
                  flexDirection: ['column', 'row'],
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  mb={2}
                  sx={{
                    flexShrink: '0',
                    width: ['100%', null, 'auto'],
                    'a, button': {
                      minWidth: '264px',
                      textAlign: 'center',
                      width: ['100%', null, 'auto'],
                    },
                  }}
                >
                  {ctaPrimary && <CallToAction content={ctaPrimary} />}
                  {!ctaPrimary && ctaBook && (
                    <ListingAddToCartButton
                      label="Book now"
                      showPrice={true}
                      sku={ctaBook.sku}
                    />
                  )}
                </Box>
                {orderByDate && (
                  <Text
                    as="p"
                    variant="description"
                    color="greyDark"
                    mb={2}
                    sx={{ textAlign: ['center', 'left'] }}
                  >
                    Order by {moment(orderByDate).format('MMMM Do')}
                  </Text>
                )}
              </Flex>
            )}
            {canClubOnly && !clubDiscount && (
              <Text as="p" variant="body3" m={0} mt={2}>
                This is a Can Club member event. Not a member?{' '}
                <Link to={`/can-club/`}>
                  <Text
                    as="span"
                    sx={{ color: 'brandPrimary', textDecoration: 'underline' }}
                  >
                    Sign up
                  </Text>
                </Link>
              </Text>
            )}
          </Box>
        </Flex>
      </Grid>
    </Box>
  )
}

export default EventCard
